import React from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

const STATUS_OPTIONS = ['all', 'completed', 'paused', 'failed', 'running', 'pending'];

// Arabic / English labels for status values. Used by both the status
// filter pills and the in-row status badge so the run history never
// leaks raw English status strings to an Arabic user (Test 11.12).
const STATUS_LABEL_AR = {
  all: 'الكل',
  pending: 'معلق',
  running: 'جاري',
  paused: 'متوقف',
  completed: 'مكتمل',
  failed: 'فشل',
};
const STATUS_LABEL_EN = {
  all: 'All',
  pending: 'Pending',
  running: 'Running',
  paused: 'Paused',
  completed: 'Completed',
  failed: 'Failed',
};

/**
 * Sortable + filterable history table for workflow runs.
 */
export function RunHistoryTable({
  runs,
  isArabic,
  sortKey,
  sortDir,
  statusFilter,
  onSort,
  onStatusFilterChange,
  onOpen,
}) {
  const t = (en, ar) => (isArabic ? ar : en);

  const ArrowFor = ({ k }) => {
    if (sortKey !== k) return null;
    return sortDir === 'asc'
      ? <ChevronUp className="inline w-3 h-3 ms-1" />
      : <ChevronDown className="inline w-3 h-3 ms-1" />;
  };

  return (
    <div data-testid="workflows-history">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-base font-semibold hv-text">{t('Recent runs', 'التشغيلات الأخيرة')}</h2>
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="workflows-history-status-filter">
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              onClick={() => onStatusFilterChange(s)}
              className={cn(
                'text-[11px] px-2 py-0.5 rounded-full transition-colors',
                statusFilter === s ? 'hv-btn-primary' : 'hv-border hover:hv-border-strong hv-text-muted'
              )}
              data-testid={`workflows-history-filter-${s}`}
            >
              {isArabic ? STATUS_LABEL_AR[s] : STATUS_LABEL_EN[s]}
            </button>
          ))}
        </div>
      </div>

      {runs.length === 0 ? (
        <div
          className="hv-text-subtle text-sm hv-border rounded p-6 text-center font-arabic"
          data-testid="workflows-history-empty"
        >
          <div className="hv-text font-semibold mb-1">
            {statusFilter === 'all'
              ? t('No runs yet', 'لا توجد تشغيلات بعد')
              : t('No matching runs.', 'لا توجد تشغيلات مطابقة.')}
          </div>
          {statusFilter === 'all' && (
            <div className="text-xs">
              {t(
                'Run a template above to see your run history here.',
                'شغّل أحد القوالب بالأعلى لتظهر تشغيلاتك هنا.',
              )}
            </div>
          )}
          {statusFilter !== 'all' && (
            <button
              onClick={() => onStatusFilterChange('all')}
              className="mt-3 hv-btn hv-btn-primary text-xs inline-flex items-center gap-1.5"
              data-testid="workflows-history-empty-cta"
            >
              {t('Show all runs', 'عرض جميع التشغيلات')}
            </button>
          )}
        </div>
      ) : (
        <table className="hv-table">
          <thead>
            <tr>
              <SortHeader k="id" sortKey={sortKey} onSort={onSort} ArrowFor={ArrowFor}>{t('Run', 'التشغيل')}</SortHeader>
              <SortHeader k="workflow_name" sortKey={sortKey} onSort={onSort} ArrowFor={ArrowFor}>{t('Workflow', 'سير العمل')}</SortHeader>
              <SortHeader k="status" sortKey={sortKey} onSort={onSort} ArrowFor={ArrowFor}>{t('Status', 'الحالة')}</SortHeader>
              <SortHeader k="started_at" sortKey={sortKey} onSort={onSort} ArrowFor={ArrowFor}>{t('Started', 'بدأ')}</SortHeader>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} data-testid={`workflows-history-row-${r.id}`}>
                <td className="font-mono text-xs">{r.id.slice(-12)}</td>
                <td className="hv-text-muted">{r.workflow_name || r.workflow_id}</td>
                <td><StatusBadge status={r.status} isArabic={isArabic} /></td>
                <td className="hv-text-subtle">{r.started_at ? new Date(r.started_at).toLocaleString() : '—'}</td>
                <td>
                  <button
                    className="hv-btn text-xs"
                    onClick={() => onOpen(r)}
                    data-testid={`workflows-history-open-${r.id}`}
                  >
                    {t('Open', 'فتح')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function SortHeader({ k, sortKey, onSort, ArrowFor, children }) {
  return (
    <th
      onClick={() => onSort(k)}
      className="cursor-pointer select-none hover:hv-text"
      data-testid={`workflows-history-sort-${k}`}
    >
      {children}<ArrowFor k={k} />
    </th>
  );
}

function StatusBadge({ status, isArabic }) {
  const map = {
    pending:   'hv-bg-policy', running: 'hv-bg-policy',
    paused:    'hv-bg-risk',
    completed: 'hv-bg-compliant',
    failed:    'hv-bg-risk',
  };
  const cls = map[status] || 'hv-bg-policy';
  const label = (isArabic ? STATUS_LABEL_AR[status] : STATUS_LABEL_EN[status]) || status;
  return (
    <span
      className={cn('text-[10px] px-2 py-0.5 rounded uppercase tracking-wider font-arabic', cls)}
      data-testid={`workflows-history-status-${status}`}
      data-status-en={status}
    >
      {label}
    </span>
  );
}

export default RunHistoryTable;
